import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { formatMoney } from "@bw/shared/utils";
import type { CommercialCommitmentOwnerPage, CommercialOfferOwnerView } from "@bw/shared/types/generated";

const availabilityLabel: Record<CommercialOfferOwnerView["availability"], string> = {
  available: "Available",
  upcoming: "Coming soon",
  expired: "Expired",
  unpublished: "Unavailable",
};
const moduleLabel: Record<CommercialOfferOwnerView["module_key"], string> = {
  academy: "Academy",
  insights: "Insights",
  competitions: "Competitions",
};

function date(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function humanize(value: string): string {
  const label = value.replaceAll("_", " ").trim();
  return label ? `${label[0].toUpperCase()}${label.slice(1)}` : "—";
}

function Fact({ label, value }: { label: string; value: string }) {
  return <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Box>;
}

export function ModuleOfferSummary({ offer, action = true, accepted }: {
  offer: CommercialOfferOwnerView;
  action?: boolean;
  accepted?: Pick<CommercialCommitmentOwnerPage["items"][number], "accepted_at" | "accepted_by_display_name"> & {
    onOpen: () => void;
  };
}) {
  const money = offer.terms_format === "software_terms_v2"
    ? offer.economics.billing_period_total
    : offer.economics.software_price;
  const renewal = offer.terms_format === "software_terms_v2"
    ? offer.economics.renewal_mode === "auto_renew" ? "Renews automatically" : "Fixed term"
    : "Historical fixed term";
  const grace = offer.economics.grace_days === 0 ? "None" : `${offer.economics.grace_days} days`;
  const trial = offer.economics.trial_days === 0 ? "None" : `${offer.economics.trial_days} days`;
  return <Paper component="article" variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { sm: "flex-start" } }}>
        <Box>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>{offer.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {moduleLabel[offer.module_key]} module
          </Typography>
        </Box>
        {!accepted ? <Chip size="small" variant={offer.availability === "available" ? "filled" : "outlined"}
          color={offer.availability === "available" ? "success" : "default"}
          label={availabilityLabel[offer.availability]} sx={{ alignSelf: "flex-start" }} /> : null}
      </Stack>
      {accepted ? <Typography variant="body2" color="text.secondary">
        Accepted {new Date(accepted.accepted_at).toLocaleString()}
        {accepted.accepted_by_display_name ? ` by ${accepted.accepted_by_display_name}` : ""}.
      </Typography> : null}
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
          {formatMoney(money.amount_minor / 100, money.currency)}
        </Typography>
        <Typography variant="body2" color="text.secondary">per {offer.economics.cadence === "annual" ? "year" : "month"}</Typography>
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 2 }}>
        <Fact label={accepted ? "Agreed billing" : "Billing"} value={renewal} />
        <Fact label="Trial" value={trial} />
        <Fact label="Payment grace" value={grace} />
        <Fact label="Offer window" value={`${date(offer.effective_from)}${offer.effective_until ? ` – ${date(offer.effective_until)}` : " onward"}`} />
      </Box>
      <Typography variant="body2" color="text.secondary">
        Acceptance records the agreement. Payment and module access happen in a separate confirmed step.
      </Typography>
      {!action && !accepted ? <Accordion disableGutters elevation={0}
        sx={{ "&::before": { display: "none" }, borderTop: 1, borderColor: "divider" }}>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Plan details</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Stack spacing={1.5}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
              <Fact label="Billable unit" value={humanize(offer.economics.billable_unit)} />
              <Fact label="Quantity" value={String(offer.economics.unit_quantity)} />
              <Fact label="Contract periods" value={offer.economics.contract_periods === null ? "Ongoing" : String(offer.economics.contract_periods)} />
            </Box>
            {offer.economics.allowances.length ? <Stack spacing={1}>
              {offer.economics.allowances.map((allowance) => <Box key={allowance.unit_key}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{humanize(allowance.unit_key)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Included {allowance.included_quantity ?? "not specified"}; maximum {allowance.maximum_quantity ?? "not specified"} · {humanize(allowance.period)} · {humanize(allowance.over_limit_behavior)}
                </Typography>
              </Box>)}
            </Stack> : <Typography variant="body2" color="text.secondary">
              No usage allowance is defined. This does not imply unlimited use.
            </Typography>}
          </Stack>
        </AccordionDetails>
      </Accordion> : null}
      {accepted ? <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
        <Button size="small" variant="contained" onClick={accepted.onOpen}
          sx={{ width: { xs: "100%", sm: "auto" } }}>
          Open accepted copy
        </Button>
      </Box> : action ? <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
        <Button size="small" variant="contained" component={Link} to={`/commercial/offers/${offer.offer_version_id}`}
          sx={{ width: { xs: "100%", sm: "auto" } }}>
          Review exact offer
        </Button>
      </Box> : null}
    </Stack>
  </Paper>;
}
